'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle,
  ArrowForward,
  ExpandMore,
  AutoGraph,
  FactCheck,
  AccountTree,
  Visibility,
  Handyman,
  TrendingUp,
  Flag,
  AssignmentTurnedIn
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import BrandName from '../../components/BrandName';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const capabilities = [
  {
    icon: AutoGraph,
    title: 'Automated Intelligence',
    description: 'Baselines are created and loaded automatically from your organization. Real-time metric actuals flow in from the facility. When anything goes out of range (or a task becomes due), the right people are instantly notified.',
  },
  {
    icon: FactCheck,
    title: 'One Feature for Every Need',
    description: 'Whether it’s a Metric Improvement Plan (tied to a metric with gap analysis and expected impact) or a General Facility Task (like “Trim bushes around the facility”), the same powerful workflow applies.',
  },
  {
    icon: AccountTree,
    title: 'Governed Collaboration',
    description: 'Clear multi-level approvals (facility → RVP/Manager) with documented reasons ensure alignment and accountability without slowing anyone down.',
  },
  {
    icon: Visibility,
    title: 'Complete Transparency',
    description: 'Full history of every metric and every plan is visible to employees, managers, and leadership. Status notifications keep everyone in sync.',
  },
  {
    icon: Handyman,
    title: 'Actionable & Flexible',
    description: 'Pre-built improvement options + fully custom plans with tasks, owners, due dates, checklists, and evidence uploads make execution simple and trackable.',
  },
];

const outcomes = [
  {
    title: 'Faster course correction',
    description: 'Identify and resolve critical metric gaps before they become larger systemic issues.',
    icon: TrendingUp,
  },
  {
    title: 'Higher accountability',
    description: 'Engage staff directly with clearly assigned owners, tasks, and documented expectations.',
    icon: AssignmentTurnedIn,
  },
  {
    title: 'Reduced risk',
    description: 'Maintain stronger survey readiness through consistent tracking of compliance and maintenance activities.',
    icon: Flag,
  },
];

const benefits = [
  'Improved financial performance and operational efficiency',
  'A culture of continuous, measurable improvement',
  'Every metric target met, every task completed',
];

export default function MetricTaskActionPlansClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Metric & Task Action Plans | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Metric & Task Action Plans (MTAP)"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            Drive Measurable Results. Own Every Task. Transform Facility Performance.
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 900,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            Empower every skilled nursing facility to achieve operational excellence by turning data-driven insights into consistent, accountable action — so that every metric's target is met, every task is completed, and every resident receives the highest standard of care in a thriving, financially strong environment.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center" justifyContent="center">
            <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
                Why Metric & Task Action Plans Changes Everything
              </Typography>
              <Typography sx={{ color: '#666', mb: 3, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
                In today’s skilled nursing environment, hitting targets for Skilled Mix, HPPD, EBITDA cash flow, and dozens of other critical metrics is no longer optional — it’s survival. At the same time, facilities must stay on top of hundreds of day-to-day tasks: preventive maintenance, compliance activities, landscaping, staff training, and more.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 10 }}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                  borderLeft: `4px solid ${BRAND_BLUE}`,
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontStyle: 'italic',
                    color: BRAND_BLUE,
                    fontSize: '1.15rem',
                    lineHeight: 1.6,
                    fontWeight: 500,
                  }}
                >
                  Metric & Task Action Plans (MTAP) gives facility leaders and teams a single, intelligent platform to set baselines, monitor performance, trigger improvement plans, and execute both metric-driven initiatives and everyday operational tasks with full visibility.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Unified Solution Capabilities
          </Typography>
        </Box>
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, 
            gap: 3,
            alignItems: 'stretch'
          }}
        >
          {capabilities.map((cap) => (
            <Paper
              key={cap.title}
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                },
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  bgcolor: `${BRAND_BLUE}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                {cap.title}
              </Typography>
              <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                {cap.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Results / Outcomes */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                The Result for Your Facility
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
                {outcomes.map((item) => (
                  <Box key={item.title} sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: 'fit-content' }}>
                      <item.icon sx={{ color: BRAND_GREEN }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{item.title}</Typography>
                      <Typography sx={{ color: '#555', fontSize: '0.95rem', mt: 0.5 }}>{item.description}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {benefits.map((benefit) => (
                <Box key={benefit} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 600, color: '#444' }}>{benefit}</Typography>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, fontSize: '1.5rem', lineHeight: 1.4 }}>
                  Metric & Task Action Plans (MTAP) turns "We should do something about that" into:
                </Typography>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 3, borderRadius: 2, mb: 4, borderLeft: '4px solid #a8d86a' }}>
                  <Typography sx={{ fontSize: '1.15rem', fontStyle: 'italic', lineHeight: 1.6 }}>
                    "Here's exactly what we're doing, who's doing it, and when it will be done."
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="One platform" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }} />
                  <Chip label="Every metric" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }} />
                  <Chip label="Every task" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }} />
                  <Chip label="Real results" sx={{ bgcolor: 'rgba(99,168,33,0.3)', color: '#a8d86a', fontWeight: 700, border: '1px solid #63A821' }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Transform Facility Performance?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> gives facility leaders and teams a single, intelligent platform to set baselines, monitor performance, and execute.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link href="/contact" style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: BRAND_GREEN,
                    color: 'white',
                    px: 5,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: '30px',
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    '&:hover': { bgcolor: '#528a1b' },
                    boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                  }}
                >
                  REQUEST A DEMO
                </Button>
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
